// Граница панели, которую тянут мышью (Drawer/README «Табеля», PanelResizer): role="separator",
// ← → — шаг, Home и End — пределы, двойное нажатие — ширина по умолчанию. Захват указателя
// держит перетаскивание на ручке, даже когда указатель ушёл с неё. Вид — у того, кто ставит
// ручку (className); пока тянут, у ручки data-dragging.
import { useRef, useState } from 'react';
import { cx } from '../lib/cx.ts';

export interface PanelResizerProps {
  /** Подпись для чтения с экрана: «Ширина меню». */
  label: string;
  /** Ширина сейчас, px. */
  value: number;
  min: number;
  max: number;
  /** Шаг ← →, px. */
  step: number;
  /** Ширина по умолчанию: её возвращает двойное нажатие. Нет — двойное нажатие ничего не делает. */
  defaultValue?: number;
  /** Новая ширина — уже в пределах. Во время перетаскивания — на каждое движение. */
  onResize: (value: number) => void;
  /** Ширина выбрана: указатель отпущен, нажата клавиша или двойное нажатие — её можно сохранить. */
  onCommit?: (value: number) => void;
  className?: string;
  title?: string;
}

function clamp(value: number, min: number, max: number): number {
  return Math.round(Math.min(Math.max(value, min), Math.max(min, max)));
}

export function PanelResizer({
  label,
  value,
  min,
  max,
  step,
  defaultValue,
  onResize,
  onCommit,
  className,
  title,
}: PanelResizerProps) {
  const drag = useRef<{ x: number; value: number; last: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const stop = () => {
    const done = drag.current;
    drag.current = null;
    setDragging(false);
    if (done) onCommit?.(done.last);
  };
  return (
    <div
      className={cx(className)}
      role="separator"
      tabIndex={0}
      aria-label={label}
      aria-orientation="vertical"
      aria-valuenow={clamp(value, min, max)}
      aria-valuemin={min}
      aria-valuemax={Math.max(min, max)}
      data-dragging={dragging ? '' : undefined}
      title={title}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.focus();
        drag.current = { x: event.clientX, value, last: value };
        setDragging(true);
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        const start = drag.current;
        if (!start) return;
        const next = clamp(start.value + event.clientX - start.x, min, max);
        if (next === start.last) return;
        start.last = next;
        onResize(next);
      }}
      onPointerUp={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        stop();
      }}
      onLostPointerCapture={stop}
      onDoubleClick={() => {
        if (defaultValue === undefined) return;
        const next = clamp(defaultValue, min, max);
        onResize(next);
        onCommit?.(next);
      }}
      onKeyDown={(event) => {
        const next =
          event.key === 'ArrowRight'
            ? value + step
            : event.key === 'ArrowLeft'
              ? value - step
              : event.key === 'Home'
                ? min
                : event.key === 'End'
                  ? max
                  : null;
        if (next === null) return;
        event.preventDefault();
        event.stopPropagation();
        const clamped = clamp(next, min, max);
        onResize(clamped);
        onCommit?.(clamped);
      }}
    />
  );
}
